import { Injectable, NgZone } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class TableAlignmentService {
  
  private observer: MutationObserver | null = null;
  private isRunning = false;
  private injectedRules: Set<string> = new Set();
  private styleEl: HTMLStyleElement | null = null;

  constructor(private ngZone: NgZone) {}

  public init() {
    if (this.isRunning) return;
    this.isRunning = true;

    // Run outside Angular zone to avoid triggering change detection on every DOM mutation
    this.ngZone.runOutsideAngular(() => {
      this.observer = new MutationObserver((mutations) => {
        let shouldProcess = false;
        for (const mutation of mutations) {
          if (mutation.addedNodes.length > 0 || mutation.type === 'attributes') {
            shouldProcess = true;
            break;
          }
        }
        if (shouldProcess) {
          this.alignTables();
        }
      });

      this.observer.observe(document.body, { 
        childList: true, 
        subtree: true, 
        attributes: true, 
        attributeFilter: ['style', 'class', 'hidden'] 
      });
      
      // Initial run
      setTimeout(() => this.alignTables(), 1000);
    });
  }

  private alignTables() {
    // Hide auto-responsive-wrappers whose wrapped table is hidden
    const wrappers = document.querySelectorAll('.auto-responsive-wrapper');
    wrappers.forEach(wrapper => {
      const wEl = wrapper as HTMLElement;
      const childTable = wrapper.querySelector('table, mat-table, .mat-mdc-table') as HTMLElement;
      if (childTable) {
        const isHidden = childTable.style.display === 'none' ||
                         childTable.hasAttribute('hidden') ||
                         childTable.classList.contains('d-none') ||
                         window.getComputedStyle(childTable).display === 'none';
        if (isHidden) {
          wEl.style.display = 'none';
        } else {
          wEl.style.display = '';
        }
      }
    });

    // 1. Standard HTML Tables
    const tables = document.querySelectorAll('table:not([data-aligned="true"])');
    tables.forEach((table) => {
      const tableId = `auto-aligned-table-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      table.setAttribute('data-aligned', 'true');
      table.classList.add(tableId);

      this.wrapTableIfNeeded(table);

      const ths = table.querySelectorAll('th');
      if (ths.length > 0) {
        ths.forEach((th, colIndex) => {
          const text = (th.textContent || '').trim().toLowerCase();
          if (!text) return;

          let align = 'left';
          if (this.isDate(text)) align = 'center';
          else if (this.isNumber(text)) align = 'right';

          if (align !== 'left') {
            const justify = align === 'right' ? 'flex-end' : (align === 'center' ? 'center' : 'flex-start');
            const rule = `html body table.${tableId} > thead > tr > th:nth-child(${colIndex + 1}), html body table.${tableId} > tbody > tr:not(:has(>[colspan])) > td:nth-child(${colIndex + 1}), html body .${tableId} > thead > tr > th:nth-child(${colIndex + 1}), html body .${tableId} > tbody > tr:not(:has(>[colspan])) > td:nth-child(${colIndex + 1}) { text-align: ${align} !important; justify-content: ${justify} !important; }`;
            this.injectStyleRule(rule);
          }
        });
      }
    });

    // 2. Angular Material Tables
    const matTables = document.querySelectorAll('mat-table:not([data-responsive="true"]), .mat-mdc-table:not([data-responsive="true"])');
    matTables.forEach(t => {
      t.setAttribute('data-responsive', 'true');
      this.wrapTableIfNeeded(t);
    });

    const matHeaders = document.querySelectorAll('mat-header-cell:not([data-aligned="true"])');
    matHeaders.forEach(header => {
      header.setAttribute('data-aligned', 'true');
      const text = (header.textContent || '').trim().toLowerCase();
      if (!text) return;

      let align = 'left';
      let justify = 'flex-start';

      if (this.isDate(text)) {
        align = 'center';
        justify = 'center';
      } else if (this.isNumber(text)) {
        align = 'right';
        justify = 'flex-end';
      }

      if (align !== 'left') {
        const h = header as HTMLElement;
        h.style.justifyContent = justify;
        h.style.textAlign = align;
        
        // Find the column class (mat-column-X or cdk-column-X)
        const colClass = Array.from(header.classList).find(c => c.startsWith('mat-column-') || c.startsWith('cdk-column-'));
        if (colClass) {
          const selectors = [
            `html body mat-table .${colClass}`,
            `html body .mat-mdc-table .${colClass}`,
            `html body table .${colClass}`,
            `html body .${colClass}`,
            `html body .${colClass}.grid-cell`,
            `html body .${colClass}.grid-header`,
            `html body .${colClass}.mat-cell`,
            `html body .${colClass}.mat-header-cell`,
            `html body .${colClass}.mat-footer-cell`
          ].join(', ');

          const sortSelectors = [
            `html body mat-table .${colClass} .mat-sort-header-container`,
            `html body .mat-mdc-table .${colClass} .mat-sort-header-container`,
            `html body .${colClass} .mat-sort-header-container`,
            `html body .${colClass}.grid-header .mat-sort-header-container`
          ].join(', ');

          this.injectStyleRule(`${selectors} { justify-content: ${justify} !important; text-align: ${align} !important; }`);
          this.injectStyleRule(`${sortSelectors} { justify-content: ${justify} !important; }`);
        }
      }
    });
  }

  private injectStyleRule(rule: string) {
    if (this.injectedRules.has(rule)) return;
    this.injectedRules.add(rule);

    if (!this.styleEl) {
      this.styleEl = document.createElement('style');
      document.head.appendChild(this.styleEl);
    }
    try {
      this.styleEl.sheet?.insertRule(rule, this.styleEl.sheet.cssRules.length);
    } catch (e) {
      console.warn('Failed to insert CSS rule:', rule, e);
    }
  }

  private wrapTableIfNeeded(table: Element) {
    if (table.parentElement && !table.parentElement.classList.contains('table-responsive')) {
      const htmlTable = table as HTMLElement;
      const isHidden = htmlTable.style.display === 'none' ||
                       htmlTable.hasAttribute('hidden') ||
                       htmlTable.classList.contains('d-none') ||
                       window.getComputedStyle(htmlTable).display === 'none';

      const wrapper = document.createElement('div');
      wrapper.className = 'table-responsive auto-responsive-wrapper';
      wrapper.style.width = '100%';
      wrapper.style.overflowX = 'auto';
      wrapper.style.setProperty('-webkit-overflow-scrolling', 'touch');
      if (isHidden) {
        wrapper.style.display = 'none';
      }
      
      table.parentElement.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  private isDate(text: string): boolean {
    const keywords = ['date', 'dob', 'time', 'created on', 'updated on', 'valid till', 'expiry', 'assign on', 'assigned on', 'disbursed on', 'approved on', 'rejected on', 'login on', 'sanction on', 'due on', 'month', 'year', 'day', 'timestamp'];
    return keywords.some(k => text.includes(k)) || /\bon\b/.test(text) || /\bdate\b/.test(text);
  }

  private isNumber(text: string): boolean {
    const keywords = [
      'amount', 'balance', 'emi', 'tenure', 'rate', 'count', 'no', 'no.', 'id', 
      'code', 'pin', 'phone', 'mobile', 'qty', 'quantity', 'price', 'total', 
      'subtotal', 'tax', 'fee', 'penalty', 'charge', 'value', 'score', 'limit', 
      'margin', 'ltv', 'roi', 'sr', 'sr.', 'sr no', '#', 'gst', 'pan', 'aadhar', 
      'pincode', 'zip', 'num', 'age', 'percentage', '%', 'sanctioned', 'approved',
      'required', 'disbursed', 'interest', 'principal', 'int', 'outstanding', 'overdue'
    ];
    
    // Clean string to find whole words
    const words = text.replace(/[^a-z0-9\s#%]/g, ' ').split(/\s+/);
    
    return words.some(w => keywords.includes(w)) || keywords.includes(text);
  }
}
